import React, { useEffect, useState, useMemo } from 'react';
import './FreiwuenscheUrlaub.css';
import { getOpenTimeOffRequestsCount, getAllTimeOffRequests, deleteTimeOffRequest } from '../services/firebaseAuthService';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import de from 'date-fns/locale/de';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth } from '../firebaseConfig';

const locales = {
  'de': de,
};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

function FreiwuenscheUrlaub() {
  const [offeneAntraege, setOffeneAntraege] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('Freiwunsch');
  const [modalReason, setModalReason] = useState('');
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState('');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUserId(user ? user.uid : '');
      fetchOpenRequests();
      fetchAllRequests();
    });
    return () => unsubscribe();
  }, []);

  const fetchOpenRequests = async () => {
    setLoading(true);
    try {
      const count = await getOpenTimeOffRequestsCount();
      setOffeneAntraege(count);
    } catch (error) {
      setOffeneAntraege(0);
    }
    setLoading(false);
  };

  const fetchAllRequests = async () => {
    setRequestsLoading(true);
    try {
      const data = await getAllTimeOffRequests();
      setRequests(data);
    } catch (error) {
      setRequests([]);
    }
    setRequestsLoading(false);
  };

  const handleSelectSlot = (slotInfo) => {

    setSelectedDate(slotInfo.start);
    setShowModal(true);
    setModalType('Freiwunsch');
    setModalReason('');
    setModalError('');
  };

  const handleSelectEvent = (event) => {

    
    let details = `Typ: ${event.title}\nDatum: ${event.start.toLocaleDateString('de-DE')}\nStatus: ${event.status === 'approved' ? 'Genehmigt' : event.status === 'pending' ? 'Ausstehend' : 'Abgelehnt'}`;
    
    if (event.reason) {
      details += `\nGrund: ${event.reason}`;
    }
    
    if (event.status === 'rejected') {
      if (event.rejectionReason) {
        details += `\nAblehnungsgrund: ${event.rejectionReason}`;
      } else {
        details += `\nAblehnungsgrund: Kein Grund angegeben`;
      }
    }
    
    // Zeige Details des Events in einem Modal
    alert(details);
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setModalError('');

    if (!modalType || !selectedDate) {
      setModalError('Bitte Typ und Datum wählen.');
      
      return;
    }

    const projektId = localStorage.getItem('lastProjektId') || '';
    let projektName = projektId;
    try {
      const projekteRaw = localStorage.getItem('projekte');
      if (projekteRaw) {
        const projekte = JSON.parse(projekteRaw);
        const found = projekte.find(p => p.id === projektId);
        if (found) projektName = found.name;
      }
    } catch (e) { /* ignore */ }

    setModalSubmitting(true);
    try {
      const db = getFirestore();
      const user = auth.currentUser;
      if (!user) {
        setModalError('Kein Benutzer angemeldet!');
  
        setModalSubmitting(false);
        return;
      }
      const dateString = selectedDate instanceof Date
        ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
        : '';

      await addDoc(collection(db, 'timeOffRequests'), {
        type: modalType,
        reason: modalReason,
        date: dateString,
        year: selectedDate.getFullYear(),
        month: selectedDate.getMonth() + 1,
        status: 'pending',
        userId: user.uid,
        createdAt: serverTimestamp(),
        projektId: projektName
      });
      setShowModal(false);
      setModalSubmitting(false);
      fetchOpenRequests();
      fetchAllRequests();

    } catch (error) {
      setModalError('Fehler beim Absenden des Antrags: ' + (error?.message || error));
      setModalSubmitting(false);

    }
  };

  const handleDeleteRequest = async (id) => {
    if (!window.confirm('Möchten Sie diesen Antrag wirklich löschen?')) return;
    setDeleteLoadingId(id);
    try {
      await deleteTimeOffRequest(id);
      fetchOpenRequests();
      fetchAllRequests();
    } catch (error) {
      alert('Fehler beim Löschen des Antrags.');
    }
    setDeleteLoadingId(null);
  };

  const events = useMemo(() => {

    
    return requests
      .filter(req => req.userId === currentUserId)
      .map(req => {

        return {
          id: req.id,
          title: req.type === 'Urlaub' ? 'Urlaub' : 'Freiwunsch',
          start: req.date ? new Date(req.date) : undefined,
          end: req.date ? new Date(req.date) : undefined,
          allDay: true,
          status: req.status,
          bgColor: req.status === 'approved' ? '#66bb6a' : req.status === 'pending' ? '#ffb300' : req.status === 'rejected' ? '#e57373' : '#90caf9',
          textColor: '#222',
          reason: req.reason,
          rejectionReason: req.rejectionReason,
          type: req.type,
          userId: req.userId
        };
      });
  }, [requests, currentUserId]);

  const eventPropGetter = (event) => ({
    style: {
      backgroundColor: event.bgColor,
      color: event.textColor,
      borderRadius: '8px',
      border: 'none',
      fontWeight: 600,
      fontSize: '1.05rem',
      padding: '2px 6px',
      minHeight: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }
  });

  return (
    <div className="freiwuensche-urlaub-container">
      <h1 className="fu-headline">Freiwünsche und Urlaub</h1>
      <div className="fu-headline-bar" />
      <div className="fu-info-box">
        <p>
          Klicken Sie auf einen Tag im Kalender, um einen Freiwunsch oder Urlaub einzutragen.
        </p>
        <div className="fu-status-legend">
          <span className="fu-status fu-status-pending">Anfrage ausstehend</span>
          <span className="fu-status fu-status-approved">Genehmigt</span>
          <span className="fu-status fu-status-rejected">Abgelehnt</span>
        </div>
        <div className="fu-antraege-row">
          <span>Offene Anträge: <b>{loading ? '...' : offeneAntraege}</b></span>
          <button className="fu-refresh-btn" onClick={() => {fetchOpenRequests(); fetchAllRequests();}} disabled={loading || requestsLoading}>
            {(loading || requestsLoading) ? 'Aktualisiere...' : 'Daten aktualisieren'}
          </button>
        </div>
      </div>

      <div className="fu-calendar-card">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
          views={['month']}
          selectable
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventPropGetter}
          messages={{
            today: 'Heute',
            previous: 'Zurück',
            next: 'Weiter',
            month: 'Monat',
            week: 'Woche',
            day: 'Tag',
            agenda: 'Agenda',
            date: 'Datum',
            time: 'Zeit',
            event: 'Antrag',
            noEventsInRange: 'Keine Anträge in diesem Zeitraum',
          }}
          culture="de"
        />
      </div>

      {showModal && (
        <div className="fu-modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowModal(false);
          }
        }}>
          <div className="fu-modal" onClick={e => e.stopPropagation()}>
            <h2>Neuer Antrag für {selectedDate ? new Date(selectedDate).toLocaleDateString() : ''}</h2>
            <form onSubmit={handleSubmitRequest}>
              <label>
                Typ:
                <select 
                  value={modalType} 
                  onChange={e => setModalType(e.target.value)}
                  disabled={modalSubmitting}
                >
                  <option value="Freiwunsch">Freiwunsch</option>
                  <option value="Urlaub">Urlaub</option>
                </select>
              </label>
              <label>
                Grund (optional):
                <input 
                  type="text" 
                  value={modalReason} 
                  onChange={e => setModalReason(e.target.value)} 
                  placeholder="z.B. Arzttermin"
                  disabled={modalSubmitting}
                />
              </label>
              {modalError && <div className="fu-modal-error">{modalError}</div>}
              <div className="fu-modal-actions">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  disabled={modalSubmitting}
                >
                  Abbrechen
                </button>
                <button 
                  type="submit" 
                  disabled={modalSubmitting}
                >
                  {modalSubmitting ? 'Sende...' : 'Antrag stellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FreiwuenscheUrlaub;
